import { Company } from "@/types";
import { Ref } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { updateCompany } from "@/services/Settings";
import { Grid } from "@mantine/core";
import { useTranslation } from "react-i18next";
import TextInput from "@/components/atoms/TextInput";
import {
  CompanySettingsFormInput,
  CompanySettingsValidationSchema,
} from "@/components/pages/Settings/validation/CompanySettingsValidationSchema";
import Select, { transformDataToSelectOption } from "@/components/atoms/Select";
import Divider from "@/components/atoms/Divider";
import PhoneField from "@/components/atoms/PhoneInput/PhoneInput";
import { useGetCompanyTypes, useGetLanguages, useGetTimeZones } from "@/services/RegisterCompany";
import { useGetCreateOrderValues } from "@/services/Orders";

interface CompanyDetailsStepProps {
  onValidationSuccess: () => void;
  companyData?: Company;
  companyId: number;
  ref?: Ref<HTMLFormElement>;
}

function CompanyDetailsStep({ onValidationSuccess, companyData, companyId, ref }: CompanyDetailsStepProps) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { data: languages } = useGetLanguages();
  const { data: companyTypes } = useGetCompanyTypes();
  const { data: timeZones } = useGetTimeZones();
  const { data: orderValues } = useGetCreateOrderValues();

  const { handleSubmit, control } = useForm<CompanySettingsFormInput>({
    resolver: zodResolver(CompanySettingsValidationSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    values: {
      companyName: companyData?.companyName || "",
      companyType: companyData?.companyType || "TRADER",
      vatNumber: companyData?.vatNumber || "",
      leiNumber: companyData?.leiNumber || "",
      eoriNumber: companyData?.eoriNumber || "",
      exportLicense: companyData?.exportLicense || "",
      phone: companyData?.phone || "",
      email: companyData?.email || "",
      language: companyData?.language || {
        id: 1,
        name: "Norwegian",
        languageCode: "no-NB",
      },
      timeZone: companyData?.timeZone || "Europe/Oslo",
      defaultCurrency: companyData?.defaultCurrency || "",
    },
  });

  const { mutateAsync: updateCompanyMutation } = useMutation({
    mutationFn: async (data: CompanySettingsFormInput) => {
      const request = {
        ...companyData,
        ...data,
        defaultCurrency: data.defaultCurrency.toUpperCase(),
      };
      return await updateCompany(request as Company);
    },
    onSuccess: async (_) => {
      await queryClient.invalidateQueries({
        queryKey: ["getCompanyById", companyId],
      });
      onValidationSuccess();
    },
  });

  const onSubmit = async (data: CompanySettingsFormInput) => {
    await updateCompanyMutation(data);
  };

  return (
    <form ref={ref} onSubmit={handleSubmit(onSubmit)}>
      <Grid>
        <Grid.Col span={{ base: 12, md: 6, lg: 8 }}>
          <Controller
            control={control}
            name="companyName"
            render={({ field, fieldState }) => {
              return (
                <TextInput
                  {...field}
                  id="name"
                  required
                  label={t("common:companyName")}
                  value={field.value || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              );
            }}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <Controller
            control={control}
            name="companyType"
            render={({ field, fieldState }) => {
              return (
                <Select
                  {...field}
                  id="companyType"
                  label={t("common:companyType")}
                  disabled
                  data={transformDataToSelectOption(companyTypes || [], "", "", t, "enumerables:companyTypes")}
                  value={field.value || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              );
            }}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <Controller
            control={control}
            name="vatNumber"
            render={({ field, fieldState }) => {
              return (
                <TextInput
                  {...field}
                  id="vatNumber"
                  required
                  label={t("common:vatNumber")}
                  value={field.value || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              );
            }}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <Controller
            control={control}
            name="leiNumber"
            render={({ field, fieldState }) => {
              return (
                <TextInput
                  {...field}
                  id="leiNumber"
                  label={t("common:leiNumber")}
                  value={field.value || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              );
            }}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <Controller
            control={control}
            name="exportLicense"
            render={({ field, fieldState }) => {
              return (
                <TextInput
                  {...field}
                  id="exportLicense"
                  label={t("common:exportlicense")}
                  value={field.value || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              );
            }}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <Divider />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState }) => {
              return (
                <TextInput
                  {...field}
                  id="email"
                  required
                  label={t("common:email")}
                  value={field.value || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              );
            }}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Controller
            control={control}
            name="phone"
            render={({ field, fieldState }) => {
              return (
                <PhoneField
                  value={field.value.toString() || ""}
                  onChange={(value) => field.onChange(value)}
                  placeholder={t("common:phone")}
                  errormessage={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              );
            }}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <Divider />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Controller
            control={control}
            name="language"
            render={({ field, fieldState }) => {
              return (
                <Select
                  {...field}
                  id="language"
                  required
                  label={t("common:language")}
                  onChange={(value) => {
                    const selectedLanguage = languages?.find((lang) => lang.name === value);
                    field.onChange(selectedLanguage);
                  }}
                  data={transformDataToSelectOption(
                    languages || [],
                    "name",
                    "languageCode",
                    t,
                    "enumerables:languages"
                  )}
                  value={field.value.name}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              );
            }}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Controller
            control={control}
            name="timeZone"
            render={({ field, fieldState }) => {
              return (
                <Select
                  {...field}
                  id="timeZone"
                  required
                  label={t("common:timeZone")}
                  data={transformDataToSelectOption(timeZones || [], "", "", t)}
                  value={field.value || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              );
            }}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Controller
            control={control}
            name="defaultCurrency"
            render={({ field, fieldState }) => {
              return (
                <Select
                  {...field}
                  id="defaultCurrency"
                  required
                  label={t("common:currency")}
                  data={transformDataToSelectOption(
                    orderValues?.currencies || [],
                    "value",
                    "value",
                    t,
                    undefined,
                    true
                  )}
                  value={field.value?.toLowerCase() || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              );
            }}
          />
        </Grid.Col>
      </Grid>
    </form>
  );
}

export default CompanyDetailsStep;
